import React, { useEffect } from 'react';
import { useEnquiryStore } from '../store/useEnquiryStore';

interface EnquiryListProps {
  onCreateNewEnquiry?: () => void;
  onEditEnquiry?: () => void;
}

export const EnquiryList: React.FC<EnquiryListProps> = ({ onCreateNewEnquiry, onEditEnquiry }) => {
  const { 
    enquiries, 
    loadAllEnquiries, 
    loadEnquiry, 
    createNewEnquiry, 
    deleteEnquiry,
    isLoading,
    isOnline,
    testConnection,
    testStorageBucket,
    toggleOnlineMode
  } = useEnquiryStore();

  useEffect(() => {
    if (isOnline) {
      loadAllEnquiries();
    }
  }, [loadAllEnquiries, isOnline]);

  const handleLoadEnquiry = async (id: string) => {
    await loadEnquiry(id);
    // Navigate back to form (edit mode)
    if (onEditEnquiry) {
      onEditEnquiry();
    }
  };

  const handleDeleteEnquiry = async (id: string, customerName: string) => {
    if (window.confirm(`Are you sure you want to delete the enquiry for ${customerName}?`)) {
      await deleteEnquiry(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Saved Enquiries</h2>
          <p className="text-sm text-gray-600">
            {isOnline ? 'Connected to database' : 'Offline mode - using local storage'}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={testConnection}
            disabled={isLoading}
            className="px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
          >
            {isLoading ? 'Testing...' : 'Test DB'}
          </button>
          <button
            onClick={testStorageBucket}
            disabled={isLoading}
            className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {isLoading ? 'Testing...' : 'Test Storage'}
          </button>
          <button
            onClick={toggleOnlineMode}
            className={`px-3 py-2 text-sm rounded-md ${
              isOnline 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-yellow-600 text-white hover:bg-yellow-700'
            }`}
          >
            {isOnline ? 'Online' : 'Offline'}
          </button>
          <button
            onClick={onCreateNewEnquiry || createNewEnquiry}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            New Enquiry
          </button>
        </div>
      </div>


      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading enquiries...</p>
        </div>
      )}

      {/* Enquiries Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {enquiries.map((enquiry) => (
            <div
              key={enquiry.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-lg text-gray-900">
                  {enquiry.customer_name || 'Unnamed Customer'}
                </h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  enquiry.outcome_status === 'Successful' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {enquiry.outcome_status}
                </span>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600 mb-4">
                <p>Created: {formatDate(enquiry.created_at)}</p>
                {enquiry.updated_at !== enquiry.created_at && (
                  <p>Updated: {formatDate(enquiry.updated_at)}</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleLoadEnquiry(enquiry.id)}
                  className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteEnquiry(enquiry.id, enquiry.customer_name)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && enquiries.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No enquiries yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isOnline 
              ? 'Get started by creating your first enquiry.' 
              : 'Connect to database or create an enquiry in offline mode.'}
          </p>
          <div className="mt-6">
            <button
              onClick={onCreateNewEnquiry || createNewEnquiry}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Enquiry
            </button>
          </div>
        </div>
      )}

      {/* Database Status */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Database Status</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• Mode: {isOnline ? '🟢 Online (PostgreSQL)' : '🟡 Offline (Local Storage)'}</p>
          <p>• Enquiries: {enquiries.length} total</p>
          <p>• Storage: {isOnline ? 'Supabase Database' : 'Browser Local Storage'}</p>
        </div>
        {!isOnline && (
          <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
            💡 Tip: Switch to Online mode to sync with the database
          </div>
        )}
      </div>
    </div>
  );
};